import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { array, arrayOf, bool, func, number, shape, string, oneOfType } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useSortBy, useTable } from 'react-table';
import classnames from 'classnames';
import drop from 'lodash/drop';
import dropRight from 'lodash/dropRight';
import remove from 'lodash/remove';
import sortBy from 'lodash/sortBy';
import orderBy from 'lodash/orderBy';
import GeoLandingPageHeader from './GeoLandingPageHeader/GeoLandingPageHeader';
import GeoTableModifiers from './GeoTableModifiers/GeoTableModifiers';
import ApplyHeader from './ApplyLandingPage/ApplyHeader/ApplyHeader';
import ApplyLandingPage from './ApplyLandingPage/ApplyLandingPage';
import { Autocomplete, Button, Input, Tabs, Tab } from '../../../../ps-aspect-design';
import Pagination from '../../../Pagination/Pagination';
import Placeholder from '../../../Placeholder/Placeholder';
import SettingsSlideout from './GeoSettingsSlideout/SettingsSlideout';
import addAlert from 'ps-components/AlertToast/addAlert';
import { states } from '../constants';
import { numberWithCommas } from '../../../lib/formatters';
import debounce from 'lodash/debounce';
import {
  addFavoriteLocation,
  clearAlert,
  clearEmployeeFilter,
  downloadToCSV,
  getCompanyLocations,
  getFavoriteLocations,
  getPayScaleLocations,
  getSupportedPayScaleLocations,
  getTrendSettings,
  mapCompanyLocationToPayScaleLocation,
  removeFavoriteLocation,
  saveGeoDifferentials,
  saveTrendSettings,
  searchPayScaleLocations
} from './GeoDifferentialActions';
import {
  APPLY_DIFF_TAB,
  COMPANY_LOADING_TEXT,
  COMPANY_LOCATIONS_TAB,
  EXPLORE_DIFF_TAB,
  FAVORITE_IMG_PATH,
  FAVORITE_LOADING_TEXT,
  FAVORITE_LOCATIONS_TAB,
  FAVORITE_PRIMARY_TEXT,
  FAVORITE_SECONDARY_TEXT,
  GENERIC_LOCATION_LOADING_TEXT,
  GEO_TREND_COLUMN_HEADER,
  LOADING_IMG_PATH,
  NOT_SET_LOCATION_KEY,
  PAYSCALE_IMG_PATH,
  PAYSCALE_LOADING_TEXT,
  PAYSCALE_LOCATIONS_TAB,
  PAYSCALE_PRIMARY_TEXT,
  RESULTS_LIMIT,
  applySearch,
  getDefaultAppliedGeoDiffs,
  mergeDiffs,
  searchMatchesLocationString,
  toCompareWithLocationValue,
  toPayScaleLocationDisplay,
  transformDiffValues
} from './helpers';
import './GeoLandingPage.scss';
import ReviewImpact from './ReviewImpact/ReviewImpact';
import DataMethodologySlideout from './DataMethodology/DataMethodologySlideout';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';

// alias the lodash function so it does not collide with
// the useTable sortBy state
const SORT_FUNCTION = sortBy;

GeoLandingPage.propTypes = {
  actions: shape({
    getCompanyLocations: func,
    getTrendSettings: func,
    mapCompanyLocationToPayScaleLocation: func,
    getPayScaleLocations: func,
    getFavoriteLocations: func,
    addFavoriteLocation: func,
    removeFavoriteLocation: func,
    searchPayScaleLocations: func,
    saveTrendSettings: func
  }),
  isApplyLocationsLoaded: bool,
  isPayScalePulseEnabled: bool,
  loading: bool,
  landingPageURL: string,
  orgLocations: arrayOf(
    shape({
      city: string,
      state: string,
      totalEmployees: string,
      totalLocations: string,
      trendData: arrayOf(
        shape({
          jobFamily: string,
          trends: arrayOf(
            shape({
              geoDifferential: number,
              quarter: number,
              sampleSize: number,
              year: number
            })
          )
        })
      )
    })
  ),
  settings: shape({
    trendRange: number,
    trendRangeType: string,
    compareWithLocation: string
  }),
  favoriteLocations: array,
  isSettingsLoaded: bool,
  isCompanyLocationsLoaded: bool,
  isPayScaleLocationsLoaded: bool,
  isFavoriteLocationsLoaded: bool,
  payScaleLocationsLoading: bool,
  isSearchingPayscaleLocations: bool,
  groupId: number,
  employeeFilters: arrayOf(
    shape({
      element: string,
      operator: string,
      value: oneOfType([number, string]),
      type: string,
      sourceField: string,
      dataSource: string
    })
  ),
  readOnlyUser: bool
};

function GeoLandingPage(props) {
  const {
    actions,
    alertType,
    applyLocationColumns,
    applyLocations,
    companyLocationColumns,
    companyLocations,
    csvLoading,
    employeeFilters,
    favoriteLocations,
    groupId,
    history,
    isApplyLocationsLoaded,
    isCompanyLocationsLoaded,
    isCompanyLocationTabEnabled,
    isFavoriteLocationsLoaded,
    isPayScaleLocationsLoaded,
    isPayScalePulseEnabled,
    isSavingGeoDifferentials,
    isSearchingPayscaleLocations,
    isSettingsLoaded,
    landingPageURL,
    loading,
    match,
    payScaleLocationColumns,
    settings,
    showInternalDataSetting,
    supportedLocations,
    totalEmployees,
    totalLocations,
    favoriteLocationsErrMsg,
    favoriteLocationsErrType,
    companyLocationsErrMsg,
    companyLocationsErrType,
    supportedLocationsErrMsg,
    supportedLocationsErrType,
    applyLocationsErrMsg,
    applyLocationsErrType,
    readOnlyUser
  } = props;

  const [toggledRows, setToggledRows] = useState([]);
  const [toggledViewAll, setViewAll] = useState([]);
  const [selectedApplyStrategy, setSelectedApplyStrategy] = useState(null);
  const [selectedExploreTab, setSelectedExploreTab] = useState(PAYSCALE_LOCATIONS_TAB);
  const [selectedGeoFeatureTab, setSelectedGeoFeatureTab] = useState(EXPLORE_DIFF_TAB);
  const [locationsBeingMapped, setLocationsBeingMapped] = useState({});
  const [searchText, setSearchText] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [unmatchedLocations, setUnmatchedLocations] = useState([]);
  const [matchLocationSearchResults, setMatchLocationSearchResults] = useState();
  const [isMappingLocation, setIsMappingLocation] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [appliedGeoDiffs, setAppliedGeoDiffs] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [hasGeoOverrides, setHasGeoOverrides] = useState(false);
  const [showUnmatchedOnly, setShowUnmatchedOnly] = useState(false);
  const [showSettingsSlideout, setShowSettingsSlideout] = useState(false);
  const [showDataMethodologySlideout, setShowDataMethodologySlideout] = useState(false);
  const [companyLocationsBannerDisplayed, setCompanyLocationsBannerDisplayed] = useState(false);
  const [applyLocationsBannerDisplayed, setApplyLocationsBannerDisplayed] = useState(false);

  const searchInputRef = useRef();

  const { groupByJobFamily, internalData, showGrouped, trendRange, trendRangeType, compareWithLocation } = settings;
  const geoTrendColumnIndex = getColumns()?.findIndex(column => column.Header === GEO_TREND_COLUMN_HEADER);
  const geoTrendColumns = getColumns()[geoTrendColumnIndex]?.columns || [];
  const hasData = filteredLocations.length !== 0;
  const hasFavoriteLocations = favoriteLocations.length !== 0;
  const showApplyGeo = selectedGeoFeatureTab === APPLY_DIFF_TAB;
  const showExploreGeo = selectedGeoFeatureTab === EXPLORE_DIFF_TAB;
  const showCompanyLocations = selectedExploreTab === COMPANY_LOCATIONS_TAB && isCompanyLocationTabEnabled;
  const showFavoriteLocations = isCompanyLocationTabEnabled
    ? selectedExploreTab === FAVORITE_LOCATIONS_TAB
    : selectedExploreTab === COMPANY_LOCATIONS_TAB;
  const showPayScaleLocations = selectedExploreTab === PAYSCALE_LOCATIONS_TAB;

  const noDataOnEditGeo =
    showApplyGeo &&
    !showReview &&
    props.employeeFilters?.length > 0 &&
    props.isCompanyLocationsLoaded &&
    !props.isCompanyLocationsLoading &&
    props.companyLocations?.length === 0;

  const showLoadingState =
    loading &&
    !isTabLoaded() &&
    (!showPayScaleLocations || (showPayScaleLocations && !initialLoad)) &&
    !isMappingLocation;

  const debouncedPayScaleLocations = useCallback(debounce(getLocationMappingSearchResults, 750), [
    isSearchingPayscaleLocations,
    matchLocationSearchResults,
    supportedLocations
  ]);

  const LOADING_PRIMARY_TEXT = showPayScaleLocations
    ? PAYSCALE_LOADING_TEXT
    : showCompanyLocations
    ? COMPANY_LOADING_TEXT
    : FAVORITE_LOADING_TEXT;

  useEffect(() => {
    actions.getTrendSettings().then(() => {
      actions.getFavoriteLocations();
    });

    actions.getSupportedPayScaleLocations();

    return () => {
      actions.clearEmployeeFilter();
    };
  }, []);

  useEffect(() => {
    setInitialLoad(true);
    setViewAll([]);
    if (toggledRows.length !== filteredLocations.length) setToggledRows([]);
  }, [selectedExploreTab, setSelectedGeoFeatureTab]);

  useEffect(() => {
    if (supportedLocationsErrMsg && supportedLocationsErrType) {
      addAlert(supportedLocationsErrMsg, supportedLocationsErrType);
      actions.clearAlert('supportedLocationsErrMsg');
    }
  }, [supportedLocationsErrMsg, supportedLocationsErrType]);

  useEffect(() => {
    if (isCompanyLocationsLoaded && companyLocations && initialLoad) {
      if (showCompanyLocations) {
        const initialLocationMappingEdits = {};
        companyLocations.forEach(orgLocation => {
          if (!orgLocation?.trendData?.length && !orgLocation.mappedPayScaleLocation) {
            const key = getLocationKey(orgLocation);
            if (key !== NOT_SET_LOCATION_KEY) {
              const mapValue = {
                value: '',
                ref: null
              };
              initialLocationMappingEdits[key] = mapValue;
            }
          }
        });
        setLocationsBeingMapped(initialLocationMappingEdits);
      }
      setInitialLoad(false);
    }
  }, [isCompanyLocationsLoaded, initialLoad]);

  useEffect(() => {
    if (trendRange && trendRangeType) {
      getLocations(true);
    }
  }, [
    trendRange,
    trendRangeType,
    groupByJobFamily,
    showGrouped,
    internalData,
    compareWithLocation,
    groupId,
    showApplyGeo,
    employeeFilters
  ]);

  useEffect(() => {
    if (showCompanyLocations && isCompanyLocationsLoaded) {
      const showedMatchingAlert = window.localStorage.getItem('showedMatchingAlert');
      const unmatchedLocations = companyLocations.filter(location => location.trendData.length === 0);
      if (
        unmatchedLocations.length === 1 &&
        unmatchedLocations[0].formattedLocation === 'Not Set' &&
        companyLocations.length > 1
      ) {
        return;
      }

      if (showedMatchingAlert !== 'true' && showCompanyLocations) showAlertMessage();
    }
  }, [alertType, showCompanyLocations, isCompanyLocationsLoaded]);

  useEffect(() => {
    if (showSettingsSlideout && alertType === 'failure') {
      setTimeout(actions.clearAlert, 7000);
    }
  }, [showSettingsSlideout]);

  useEffect(() => {
    if (showFavoriteLocations) {
      if (favoriteLocationsErrMsg && favoriteLocationsErrType) {
        addAlert(favoriteLocationsErrMsg, favoriteLocationsErrType);
        actions.clearAlert('favoriteLocationsErrMsg');
      }

      const filteredLocations = applySearch(searchText, favoriteLocations, settings);
      setFilteredLocations(filteredLocations);
    }
  }, [searchText, favoriteLocations, selectedExploreTab]);

  useEffect(() => {
    if (showCompanyLocations) {
      if (companyLocationsErrMsg && companyLocationsErrType && !companyLocationsBannerDisplayed) {
        addAlert(companyLocationsErrMsg, companyLocationsErrType);
        actions.clearAlert('companyLocationsErrMsg');
        setCompanyLocationsBannerDisplayed(true);
      }
      const filteredLocations = applySearch(searchText, companyLocations, settings);
      setFilteredLocations(filteredLocations);
      if (unmatchedLocations.length) {
        const locations = filteredLocations.filter(location => !isSupportedLocation(location));
        setUnmatchedLocations(locations);
      }
    }

    if (showApplyGeo) {
      if (applyLocationsErrMsg && applyLocationsErrType && !applyLocationsBannerDisplayed) {
        addAlert(applyLocationsErrMsg, applyLocationsErrType);
        actions.clearAlert('applyLocationsErrMsg');
        setApplyLocationsBannerDisplayed(true);
      }
    }
  }, [searchText, companyLocations, selectedExploreTab, selectedGeoFeatureTab]);

  useEffect(() => {
    if (showPayScaleLocations) {
      setFilteredLocations([]);
    } else {
      getLocations();
    }

    setTablePage(1);
  }, [selectedGeoFeatureTab]);

  useEffect(() => {
    if (showApplyGeo) {
      const filteredLocations = applySearch(searchText, applyLocations, settings);
      setFilteredLocations(filteredLocations);
      if (unmatchedLocations.length) {
        const locations = filteredLocations.filter(location => !isSupportedLocation(location));
        setUnmatchedLocations(locations);
      }
    }
  }, [searchText, applyLocations, selectedGeoFeatureTab]);

  useEffect(() => {
    if (showApplyGeo) {
      const defaultAppliedGeoDiffs = getDefaultAppliedGeoDiffs(applyLocations);
      setAppliedGeoDiffs(mergeDiffs(defaultAppliedGeoDiffs, appliedGeoDiffs));
      setHasGeoOverrides(true);
    }
  }, [selectedGeoFeatureTab, applyLocations]);

  useEffect(() => {
    setHasGeoOverrides(true);
  }, [showReview]);

  useEffect(() => {
    getPayScaleLocations();
  }, [searchText, trendRange, trendRangeType, groupByJobFamily, showGrouped]);

  useEffect(() => {
    if (!initialLoad) {
      setSelectedApplyStrategy(null);
      setSearchText('');
    }
  }, [selectedGeoFeatureTab]);

  function getColumns() {
    return showApplyGeo
      ? applyLocationColumns
      : showCompanyLocations
      ? companyLocationColumns
      : payScaleLocationColumns;
  }

  function getPayScaleLocations() {
    if (showPayScaleLocations && isSettingsLoaded && !showApplyGeo) {
      if (searchText.length && searchText.trim().length) {
        const locations = [...supportedLocations].filter(location =>
          searchMatchesLocationString(toPayScaleLocationDisplay(location), searchText.toLowerCase())
        );
        actions.getPayScaleLocations(locations).then(res => {
          setFilteredLocations(res);
        });
      } else {
        setFilteredLocations([]);
      }
    }
  }

  function isTabLoaded() {
    if (showPayScaleLocations) {
      return isPayScaleLocationsLoaded;
    } else if (showCompanyLocations) {
      return isCompanyLocationsLoaded;
    } else if (showFavoriteLocations) {
      return isFavoriteLocationsLoaded;
    } else if (showApplyGeo) {
      return isApplyLocationsLoaded;
    }
    return false;
  }

  function isSupportedLocation(location) {
    return supportedLocations.has(location.payScaleLocation);
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy }
  } = useTable(
    {
      columns: getColumns(),
      data: unmatchedLocations.length ? unmatchedLocations : filteredLocations,
      initialState: {
        sortBy: [
          { desc: true, id: 'totalEmployees' },
          { desc: false, id: 'formattedLocation' }
        ]
      }
    },
    useSortBy
  );

  function handleExploreTabSwitch(tab) {
    if (tab !== selectedExploreTab) {
      searchInputRef.current?.clearSearchValue();
      setSelectedExploreTab(tab);
      tab === COMPANY_LOCATIONS_TAB && isCompanyLocationTabEnabled
        ? history.push(`${match.path}/company-locations`)
        : tab === PAYSCALE_LOCATIONS_TAB
        ? history.replace(match.path)
        : history.push(`${match.path}/favorite-locations`);
    }
  }

  function handleFeatureTabSwitch(tab) {
    if (tab !== selectedGeoFeatureTab) {
      setSelectedGeoFeatureTab(tab);
      setShowReview(false);
      tab === EXPLORE_DIFF_TAB ? history.replace(match.path) : history.push(`${match.path}/select-strategy`);
    }
  }

  function handleStrategyCardClick(strategy) {
    if (strategy === 'employees') {
      history.push(`${match.path}/apply`);
      setSelectedApplyStrategy(strategy);
    }
  }

  function getLocations(forceUpdate) {
    if (forceUpdate) {
      actions.getFavoriteLocations();
      if (isCompanyLocationTabEnabled) actions.getCompanyLocations(showApplyGeo);
      getPayScaleLocations();
      return;
    }
    if (
      (showCompanyLocations && isCompanyLocationsLoaded) ||
      (showFavoriteLocations && isFavoriteLocationsLoaded) ||
      (showApplyGeo && isApplyLocationsLoaded)
    ) {
      return;
    }

    if (showFavoriteLocations) {
      actions.getFavoriteLocations();
    } else {
      getPayScaleLocations();
    }
  }

  function getSortedTrends(trendData) {
    if (sortBy.length > 0 && !sortBy[0].desc && sortBy[0].id !== 'formattedLocation') {
      return SORT_FUNCTION(trendData, [sortBy[0].id, 'formattedLocation']);
    } else if (sortBy.length > 0 && sortBy[0].desc && sortBy[0].id !== 'formattedLocation') {
      return orderBy(trendData, [item => item[sortBy[0].id], item => item.formattedLocation], ['desc', 'asc']);
    } else {
      return trendData;
    }
  }

  function getLocationCell(datum) {
    if (groupByJobFamily) return datum.jobFamily;
    else return datum.formattedLocation;
  }

  function renderSubRows(data, groupId, location) {
    const defaultLimit = showGrouped && !groupByJobFamily ? 2 : 3;
    const limit = toggledViewAll.includes(groupId) ? data?.length : defaultLimit;

    return data?.map((datum, index) => {
      const locationCell = getLocationCell(datum);
      const showRow = index <= limit && locationCell !== 'All';
      const isLocationFavorited = props.favoriteLocations.some(location => location.formattedLocation);

      if (showRow) {
        return (
          <tr className="geo-table__row geo-location__subrow">
            {!showCompanyLocations && !showApplyGeo ? (
              <td className="geo-table__cell favorite-cell">
                {!showGrouped ||
                  (showGrouped && !groupByJobFamily && (
                    <i
                      className={isLocationFavorited ? 'icon-star' : 'icon-star-empty'}
                      onClick={() =>
                        isLocationFavorited ? removeFavoriteLocation(locationCell) : addFavoriteLocation(locationCell)
                      }
                    />
                  ))}
              </td>
            ) : (
              <td className="geo-table__cell" />
            )}
            <td className="geo-table__cell">
              <h4 className="location-family">{locationCell}</h4>
            </td>
            {(showCompanyLocations || showApplyGeo) && ((showGrouped && groupByJobFamily) || !showGrouped) && (
              <td className="geo-table__cell" />
            )}
            <td className="geo-table__cell">{toPayScaleLocationDisplay(props.settings.compareWithLocation)}</td>
            {datum.trends.map(trend => (
              <td className="geo-table__cell center-align">
                {isUpdatingLocationMapping(location) ? (
                  <span className="loading" />
                ) : (
                  <>
                    {trend.geoDifferential > 0
                      ? `+${trend.geoDifferential.toFixed(2)}%`
                      : `${trend.geoDifferential.toFixed(2)}%`}
                  </>
                )}
              </td>
            ))}
            <td className="geo-table__cell" />
          </tr>
        );
      }
    });
  }

  function getLocationMappingSearchResults(params) {
    return actions.searchPayScaleLocations(supportedLocations, params).then(results => {
      const mappedResults = results.map(result => result.label);
      setMatchLocationSearchResults(mappedResults);
      return mappedResults;
    });
  }

  function renderViewLink(groupId) {
    const viewAll = !toggledViewAll.includes(groupId);

    return (
      <tr className="geo-table__row">
        <td className="geo-table__cell" />
        <td className="geo-table__cell" colSpan={geoTrendColumns.length + 4}>
          <a onClick={() => toggleAllSubRows(groupId)}>
            {`View ${viewAll ? 'all' : 'less'} ${groupByJobFamily ? 'job families' : 'locations'}`}
          </a>
        </td>
      </tr>
    );
  }

  function getTableRow(location) {
    const { original } = location;
    const { formattedLocation, trendData, mappedPayScaleLocation, city, state, pendingMatchLocation } = original;
    let trendCells = [];
    const geoTrendColumnIndex = getColumns()?.findIndex(column => column.Header === GEO_TREND_COLUMN_HEADER);
    if (geoTrendColumnIndex !== -1) {
      trendCells =
        showGrouped || showApplyGeo
          ? dropRight(drop(location.cells, geoTrendColumnIndex))
          : drop(location.cells, geoTrendColumnIndex);
    }
    const sortedTrends = getSortedTrends(trendData);
    const locationKey = getLocationKey(original);
    const isLocationNotSet = locationKey === NOT_SET_LOCATION_KEY;
    const payScaleLocation = `${city}, ${state}`;
    const isLocationFavorited = props.favoriteLocations.some(
      location => location.formattedLocation === payScaleLocation
    );
    const isRowToggled = toggledRows.includes(location.id);
    const exceedsTrendMinimum = sortedTrends?.length > 3;

    return (
      <Fragment>
        <tr
          className={classnames('geo-table__row', {
            'geo-location__grouped': showGrouped && !showApplyGeo,
            'no-metadata': showCompanyLocations && !groupByJobFamily
          })}
          onClick={e => toggleSubRows(e, location.id)}
        >
          {!showCompanyLocations && !showApplyGeo ? (
            <td className="geo-table__cell favorite-cell">
              {(!showGrouped || (showGrouped && groupByJobFamily)) && (
                <i
                  className={isLocationFavorited ? 'icon-star' : 'icon-star-empty'}
                  onClick={() =>
                    isLocationFavorited
                      ? removeFavoriteLocation(payScaleLocation)
                      : addFavoriteLocation(payScaleLocation)
                  }
                />
              )}
            </td>
          ) : (
            <td className="geo-table__cell" />
          )}
          <td className="geo-table__cell geo-table__cell-mapping">
            <div className="geo-location">
              <h4 className="geo-location__title">{formattedLocation}</h4>
              {(showCompanyLocations || showApplyGeo) &&
                !isEditingLocation(original) &&
                !isLocationNotSet &&
                (showApplyGeo || groupByJobFamily || !showGrouped) && (
                  <Fragment>
                    <a
                      className="edit-location-link"
                      onClick={() => editLocationMapping(original)}
                      data-for={`edit-mapping-${location.id}`}
                      data-tip
                    >
                      Edit
                    </a>
                    <ReactTooltip id={`edit-mapping-${location.id}`} effect="solid">
                      <span>Edit location match</span>
                    </ReactTooltip>
                  </Fragment>
                )}
            </div>
            {!isEditingLocation(original) &&
              !isUpdatingLocationMapping(original) &&
              mappedPayScaleLocation &&
              mappedPayScaleLocation !== `${city}, ${states[state]}` && (
                <div className="mapped-location__text">{`Matched to ${mappedPayScaleLocation}`}</div>
              )}
            {isUpdatingLocationMapping(original) && (
              <span className="mapped-location__text">
                {pendingMatchLocation ? `Matching to ${pendingMatchLocation}...` : 'Removing match'}
              </span>
            )}
            {(showCompanyLocations || showApplyGeo) &&
              isCompanyLocationsLoaded &&
              isEditingLocation(original) &&
              !isUpdatingLocationMapping(original) &&
              (showApplyGeo || groupByJobFamily || !showGrouped) && (
                <div className="edit-geo__location-mapping">
                  <div className="location-mapping__header">
                    <span>Match to</span>
                  </div>
                  <div className="location-mapping__input-container">
                    <Autocomplete
                      id={locationKey}
                      onInputChange={value => {
                        setLocationMappingInputValue(value, locationKey);
                        return debouncedPayScaleLocations({
                          params: {
                            workLocation: value,
                            excludeNational: true
                          }
                        });
                      }}
                      isLoading={isSearchingPayscaleLocations}
                      suggestions={matchLocationSearchResults}
                      placeholder={'Search location'}
                      forceSelect={true}
                      value={locationsBeingMapped[locationKey].value}
                      onClear={() => {
                        const currentInputValue = locationsBeingMapped[locationKey].value;
                        setLocationMappingInputValue('', locationKey);
                        setMatchLocationSearchResults(undefined);
                        if (original.mappedPayScaleLocation && original.mappedPayScaleLocation === currentInputValue) {
                          locationMappingChange(original, '');
                        }
                      }}
                      onSelectionChange={value => {
                        original.pendingMatchLocation = value;
                        locationMappingChange(original, value);
                      }}
                    />
                    <a className="cancel__link" onClick={clearLocationMappingInput.bind(this, original)}>
                      Cancel
                    </a>
                  </div>
                </div>
              )}
          </td>
          {(showCompanyLocations || showApplyGeo) && ((showGrouped && groupByJobFamily) || !showGrouped) && (
            <td className="geo-table__cell center-align">
              {original?.totalEmployees ? numberWithCommas(original.totalEmployees) : ''}
            </td>
          )}
          <td className="geo-table__cell">
            {(showApplyGeo || groupByJobFamily || !showGrouped) && !isLocationNotSet
              ? toPayScaleLocationDisplay(settings.compareWithLocation)
              : ' '}
          </td>
          {trendData?.length > 0 && (showApplyGeo || groupByJobFamily || !showGrouped) ? (
            trendCells?.map(cell => (
              <td className="geo-table__cell center-align trend-data">
                {isUpdatingLocationMapping(original) ? (
                  <span className="loading" />
                ) : (
                  <>{cell?.value > 0 ? `+${cell?.value?.toFixed(2)}%` : `${cell?.value?.toFixed(2)}%`}</>
                )}
              </td>
            ))
          ) : (
            <>
              <td
                className="geo-table__cell center-align location-warning"
                colSpan={!showApplyGeo && (groupByJobFamily || !showGrouped) ? trendRange + 1 : trendRange}
              ></td>
            </>
          )}
          {showGrouped && !showApplyGeo && (
            <td className="geo-table__cell group-row__toggle" width="10%">
              {!showApplyGeo && exceedsTrendMinimum && (
                <i className={!isRowToggled ? 'icon-up-open' : 'icon-down-open'} />
              )}
            </td>
          )}
          {showApplyGeo && (
            <td className="geo-table__cell adjustment-override" width="10%">
              {getGeoInput(formattedLocation)}
            </td>
          )}
        </tr>
        {!showApplyGeo && showGrouped && !isRowToggled && renderSubRows(sortedTrends, location.id, location)}
        {!showApplyGeo && showGrouped && !isRowToggled && exceedsTrendMinimum && renderViewLink(location.id)}
      </Fragment>
    );
  }

  function getGeoInput(formattedLocation) {
    let inputRef;

    return (
      <Input
        aria-label="Applied Geo Differential"
        className="adjustment__input"
        onChange={(event, value) => handleGeoChange(value, formattedLocation)}
        onBlur={(event, value) => handleGeoChange(value, formattedLocation)}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            inputRef.blur();
          }
        }}
        type="percent"
        minNumberOfDecimals={2}
        maxNumberOfDecimals={2}
        value={getAppliedGeo(formattedLocation)}
        displayFormatted={true}
        zeroOnEmpty={true}
        ref={r => (inputRef = r)}
        disabled={readOnlyUser}
      />
    );
  }

  function handleGeoChange(value, location) {
    const newGeoDiffs = [...appliedGeoDiffs];
    const foundDiff = newGeoDiffs.find(updatedDiff => location === updatedDiff.location);

    if (foundDiff && foundDiff.differential !== value) {
      foundDiff.differential = value;

      setAppliedGeoDiffs(newGeoDiffs);
      if (!hasGeoOverrides) setHasGeoOverrides(true);
    }
  }

  function setLocationMappingInputValue(value, locationKey) {
    const newLocationsBeingMapped = { ...locationsBeingMapped };
    locationsBeingMapped[locationKey].value = value;
    setLocationsBeingMapped(newLocationsBeingMapped);
  }

  function getAppliedGeo(location) {
    const found = appliedGeoDiffs?.find(updatedDiff => location === updatedDiff.location);

    return found?.differential;
  }

  function toggleSubRows(e, index) {
    if (!showGrouped || e?.target?.className?.includes('icon-star')) return;
    if (!index) {
      const rowsToAdd = Array.from({ length: companyLocations.length }, (_, i) => i.toString());
      toggledRows.length === 0 ? setToggledRows(rowsToAdd) : setToggledRows([]);
      if (toggledViewAll.length > 0) setViewAll([]);
      return;
    }

    if (
      e?.target?.className === 'edit-location-link' ||
      e?.target?.className === 'cancel__link' ||
      e?.target?.tagName === 'svg' ||
      e?.target?.className?.includes('pxl-form-control') ||
      e?.target?.className?.includes('ps-autocomplete')
    )
      return;
    if (toggledRows.includes(index)) {
      const rows = toggledRows.filter(group => group !== index);
      setToggledRows(rows);
      return;
    }
    setToggledRows([...toggledRows, index]);
  }

  function toggleAllSubRows(index) {
    if (toggledViewAll.includes(index)) {
      const rows = toggledViewAll.filter(group => group !== index);
      setViewAll(rows);
      return;
    }
    setViewAll([...toggledViewAll, index]);
  }

  function setIsUpdatingLocationMapping(location, isUpdatingFlag) {
    const newLocationsBeingMapped = { ...locationsBeingMapped };
    const key = getLocationKey(location);
    const locationBeingMapped = newLocationsBeingMapped[key];
    locationBeingMapped.isUpdating = isUpdatingFlag;
    setLocationsBeingMapped(newLocationsBeingMapped);
  }

  function isUpdatingLocationMapping(location) {
    const key = getLocationKey(location);
    return locationsBeingMapped[key]?.isUpdating === true;
  }

  function isEditingLocation(location) {
    const key = getLocationKey(location);
    return locationsBeingMapped.hasOwnProperty(key);
  }

  function clearLocationMappingInput(location) {
    const newLocationsBeingMapped = { ...locationsBeingMapped };
    const key = getLocationKey(location);
    delete newLocationsBeingMapped[key];
    setLocationsBeingMapped(newLocationsBeingMapped);
    setMatchLocationSearchResults(undefined);
  }

  function editLocationMapping(location) {
    const newLocationsBeingMapped = { ...locationsBeingMapped };
    const key = getLocationKey(location);
    newLocationsBeingMapped[key] = { value: location.mappedPayScaleLocation };
    setLocationsBeingMapped(newLocationsBeingMapped);
  }

  function locationMappingChange(location, payScaleLocation) {
    setIsUpdatingLocationMapping(location, true);
    setIsMappingLocation(true);
    props.actions
      .mapCompanyLocationToPayScaleLocation(location.formattedLocation, props.settings.internalData, payScaleLocation)
      .then(() => {
        actions.getCompanyLocations(showApplyGeo).then(({ companyLocations, applyLocations }) => {
          const newAppliedDiffs = [...appliedGeoDiffs];

          const defaultAppliedGeoDiffs = getDefaultAppliedGeoDiffs(applyLocations);
          const foundLocation = newAppliedDiffs?.find(x => x.location === location?.formattedLocation);
          if (foundLocation) {
            foundLocation.differential = defaultAppliedGeoDiffs?.find(
              x => x.location === location?.formattedLocation
            )?.differential;
          }
          setAppliedGeoDiffs(newAppliedDiffs);

          setIsUpdatingLocationMapping(location, false);
          clearLocationMappingInput(location);
          setIsMappingLocation(false);
        });
      });
  }

  function getLocationKey(location) {
    if (location?.formattedLocation !== 'Not Set') {
      return location?.formattedLocation?.replace(/ /g, '-');
    }

    return NOT_SET_LOCATION_KEY;
  }

  function showAlertMessage() {
    switch (alertType) {
      case 'failure':
        const failureMessage = (
          <div className="alert-container">
            <div className="alert-message__primary">{`Sorry, we are not able to match geo differential data with “${
              settings.internalData === 'workLocation' ? 'Work Location' : 'City/State'
            }”.`}</div>
            <div className="alert-message__secondary">
              Please change the internal data for location matching in{' '}
              <a onClick={() => setShowSettingsSlideout(true)}>Data Settings</a>
            </div>
          </div>
        );
        addAlert(failureMessage, 'warning', 'manual');
        window.localStorage.setItem('showedMatchingAlert', true);
        break;
      case 'warning':
        const warningMessage = (
          <div className="alert-container">
            <div className="alert-message__primary">
              We don’t have Geo Differential data for some company locations.
            </div>
            <div className="alert-message__secondary">
              You can manually match them with a nearby city or metro area.
            </div>
          </div>
        );
        addAlert(warningMessage, 'warning', 'manual');
        window.localStorage.setItem('showedMatchingAlert', true);
        break;
      case 'success':
        const successMessage = (
          <div className="alert-container">
            <div className="alert-message__primary">
              We have matched geo differential data with all your company locations.
            </div>
            <div className="alert-message__secondary">
              You can review and edit individual matching by clicking on Edit next to each location.
            </div>
          </div>
        );
        addAlert(successMessage, 'success', 'manual');
        window.localStorage.setItem('showedMatchingAlert', true);
        break;
      default:
        break;
    }
  }

  function addFavoriteLocation(location) {
    const alertSuccessMessage = (
      <div className="alert-container">
        <span className="alert-message__primary">{location} added to favorites</span>
      </div>
    );

    props.favoriteLocations.push({ formattedLocation: location });
    return actions
      .addFavoriteLocation(location)
      .then(() => {
        addAlert(alertSuccessMessage, 'success');
      })
      .catch(() => {
        addAlert('Error adding favorite location', 'danger');
      });
  }

  function removeFavoriteLocation(location) {
    const alertSuccessMessage = (
      <div className="alert-container">
        <span className="alert-message__primary">{location} removed from favorites&nbsp;&nbsp;</span>
        <span className="location-undo__link" onClick={() => addFavoriteLocation(location)}>
          Undo
        </span>
      </div>
    );

    remove(props.favoriteLocations, favoriteLocation => favoriteLocation.formattedLocation === location);
    return actions
      .removeFavoriteLocation(location)
      .then(() => {
        addAlert(alertSuccessMessage, 'success', 5000);
      })
      .catch(() => {
        addAlert('Error removing favorite location', 'danger');
      });
  }

  function handleCompareWithLocationChange(option) {
    settings.compareWithLocation = toCompareWithLocationValue(option.label);
    actions.saveTrendSettings(props.settings);
    getLocations(true);
  }

  const tableClassNames = classnames('geo-table__container', {
    loading: loading && !isTabLoaded() && !initialLoad && !isMappingLocation,
    'first-load': initialLoad && isCompanyLocationTabEnabled,
    'apply-geo': showApplyGeo,
    'explore-geo': !showApplyGeo
  });

  function getTableRows() {
    const startIndex = (tablePage - 1) * RESULTS_LIMIT;
    const endIndex = startIndex + RESULTS_LIMIT;
    const resultRows = rows.slice(startIndex, endIndex);
    return resultRows.map(row => {
      prepareRow(row);
      return getTableRow(row);
    });
  }

  function handleCSVDownload() {
    props.actions.downloadToCSV(
      rows,
      getColumns(),
      settings.compareWithLocation,
      sortBy[0],
      showApplyGeo && !showReview,
      showApplyGeo && showReview,
      transformDiffValues(appliedGeoDiffs)
    );
  }

  function getNoEmployeeDataPlaceholder() {
    return (
      <Placeholder
        className="pxl-text-small pxl-text-muted explore-geo__no-employees"
        secondaryText={`In order to ${
          showApplyGeo ? 'apply' : 'explore'
        } geo differentials for your company locations, please upload employee data first`}
        showText={true}
      />
    );
  }

  function getNoDataOnEditGeoMessage() {
    return (
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        primaryText="No results found with applied filters"
        secondaryText="Please modify filters and try again"
        showText={true}
      />
    );
  }

  function renderApplyTabContent() {
    if (
      props.isCompanyLocationsLoaded &&
      props.employeeFilters?.length === 0 &&
      !props.isCompanyLocationsLoading &&
      props.companyLocations?.length === 0
    ) {
      return !applyLocationsErrType ? getNoEmployeeDataPlaceholder() : renderDataFetchFailurePlaceholder();
    }

    if (!selectedApplyStrategy && !props.isCompanyLocationsLoading && props.isCompanyLocationsLoaded) {
      return <ApplyLandingPage handleStrategyCardClick={handleStrategyCardClick} />;
    }

    return (
      <>
        <div className={classnames('edit-geo__container', { review: showReview })}>
          {!showReview && props.isCompanyLocationsLoaded && !props.isCompanyLocationsLoading && (
            <ApplyHeader
              compareWithLocation={compareWithLocation}
              groupByJobFamily={groupByJobFamily}
              handleCompareWithLocationChange={handleCompareWithLocationChange}
              hasData={hasData}
              searchInputRef={searchInputRef}
              searchPayScaleLocations={actions.searchPayScaleLocations}
              setSearchText={setSearchText}
              showApplyGeo={showApplyGeo}
              showCompanyLocations={showCompanyLocations}
              showGrouped={showGrouped}
              showPayScaleLocations={showPayScaleLocations}
              showUnmatchedOnly={showUnmatchedOnly}
              supportedLocations={supportedLocations}
              toggleUnmatchedLocations={toggleUnmatchedLocations}
              totalEmployees={totalEmployees}
              totalLocations={totalLocations}
            />
          )}
          {showLoadingState && !showReview && renderPlaceholder()}
          {showReview ? (
            <ReviewImpact
              appliedGeoDiffs={transformDiffValues(appliedGeoDiffs)}
              applyGeoDiffs={handleGeoDiffSave}
              getCompanyLocations={actions.getCompanyLocations}
              hasGeoOverrides={hasGeoOverrides}
              isSavingGeoDifferentials={isSavingGeoDifferentials}
              returnToEdit={() => {
                setShowReview(false);
                actions.getCompanyLocations(showApplyGeo);
              }}
              readOnlyUser={readOnlyUser}
            />
          ) : (
            <div className={tableClassNames}>
              {hasData && !noDataOnEditGeo && renderTable()}
              {noDataOnEditGeo && getNoDataOnEditGeoMessage()}
              {searchText && !hasData && !loading && renderNoResultsMessage()}
            </div>
          )}
        </div>
        {!showReview && props.companyLocations?.length !== 0 && !props.isCompanyLocationsLoading && !showLoadingState && (
          <div className="pulse-apply-geo__footer-container edit-geo">
            <Button
              className="action-link"
              data-testid="methods-link"
              icon="Left"
              onClick={() => {
                setSelectedApplyStrategy(null);
              }}
              variant="icon-link"
            >
              Back to Methods
            </Button>
            <div className="explore-geo__pagination-container">
              <Pagination
                offset={(tablePage - 1) * RESULTS_LIMIT}
                step={RESULTS_LIMIT}
                totalCount={rows.length}
                pageAction={(limit, offset) => setTablePage(offset / limit + 1)}
                prependText="Showing results for"
                middleText="locations out of"
              />
            </div>
            <Button
              className="footer-button"
              data-testid="review-impact-button"
              icon="Right"
              iconPosition="right"
              onClick={() => setShowReview(true)}
              variant="secondary"
            >
              Review Impact
            </Button>
          </div>
        )}
      </>
    );
  }

  function handleGeoDiffSave() {
    actions.saveGeoDifferentials(appliedGeoDiffs);
    setHasGeoOverrides(false);
  }

  function renderDataFetchFailurePlaceholder() {
    const locationType =
      showApplyGeo || showCompanyLocations ? 'company' : showPayScaleLocations ? 'PayScale' : 'favorite';
    return (
      <Placeholder
        className="pxl-text-small pxl-text-muted explore-geo__no-employees"
        primaryText={`No ${locationType} location data found`}
        showText={true}
      />
    );
  }

  function renderExploreTabContent() {
    if (
      (showCompanyLocations && companyLocationsErrType) ||
      (showFavoriteLocations && favoriteLocationsErrType) ||
      (showPayScaleLocations && supportedLocationsErrType) ||
      (showApplyGeo && applyLocationsErrType)
    ) {
      return renderDataFetchFailurePlaceholder();
    }

    if (
      props.isCompanyLocationsLoaded &&
      !props.isCompanyLocationsLoading &&
      props.companyLocations?.length === 0 &&
      showCompanyLocations
    ) {
      return getNoEmployeeDataPlaceholder();
    }

    if (!hasFavoriteLocations && showFavoriteLocations) {
      return renderPlaceholder();
    }

    if (showLoadingState && !showPayScaleLocations) {
      return renderPlaceholder();
    }

    return (
      <>
        <div className="explore-geo__container">
          <GeoTableModifiers
            groupByJobFamily={groupByJobFamily}
            hasData={hasData}
            searchInputRef={searchInputRef}
            setSearchText={setSearchText}
            showCompanyLocations={showCompanyLocations}
            showGrouped={showGrouped}
            showPayScaleLocations={showPayScaleLocations}
            toggleUnmatchedLocations={toggleUnmatchedLocations}
            showUnmatchedOnly={showUnmatchedOnly}
            toggleSubRows={toggleSubRows}
            toggledRows={toggledRows}
          />
          {(showPayScaleLocations && !searchText.length) || showLoadingState ? (
            renderPlaceholder()
          ) : (
            <div className={tableClassNames}>
              {hasData && renderTable()}
              {searchText && !hasData && !loading && renderNoResultsMessage()}
            </div>
          )}
        </div>

        {hasData && (
          <div className="marketpay-survey-library__pagination-container">
            <Pagination
              className="marketpay-survey-library__pagination"
              offset={(tablePage - 1) * RESULTS_LIMIT}
              step={RESULTS_LIMIT}
              totalCount={rows.length}
              pageAction={(limit, offset) => setTablePage(offset / limit + 1)}
              prependText="Showing results for"
              middleText="locations out of"
            />
          </div>
        )}
      </>
    );
  }

  function toggleUnmatchedLocations() {
    setShowUnmatchedOnly(!showUnmatchedOnly);
    if (unmatchedLocations.length === 0) {
      const locations = filteredLocations.filter(location => !isSupportedLocation(location));
      setUnmatchedLocations(locations);
    } else {
      setUnmatchedLocations([]);
    }
  }

  function renderPlaceholder() {
    if (showApplyGeo && showLoadingState) {
      return <InPlaceIndicator />;
    }

    const imgPath = showLoadingState ? LOADING_IMG_PATH : showFavoriteLocations ? FAVORITE_IMG_PATH : PAYSCALE_IMG_PATH;
    const primaryText = showLoadingState
      ? LOADING_PRIMARY_TEXT
      : showFavoriteLocations
      ? FAVORITE_PRIMARY_TEXT
      : PAYSCALE_PRIMARY_TEXT;

    return (
      <div className="placeholder-container">
        <Placeholder
          className="pxl-text-small pxl-text-muted"
          imagePath={!showApplyGeo ? imgPath : null}
          primaryText={!showApplyGeo ? primaryText : ''}
          secondaryText={
            showFavoriteLocations && !(loading && !isTabLoaded())
              ? FAVORITE_SECONDARY_TEXT
              : showApplyGeo
              ? GENERIC_LOCATION_LOADING_TEXT
              : ''
          }
          showText={true}
        />
      </div>
    );
  }

  function renderNoResultsMessage() {
    return (
      <div className="empty-table">
        <div className="text-primary">
          No search results for
          <span className="search-term">{`"${searchText}"`}</span>
        </div>
        <div className="text-secondary">Please try a different keyword</div>
      </div>
    );
  }

  function renderTable() {
    return (
      <table {...getTableProps()} className={classnames('geo-table', { 'company-locations': showCompanyLocations })}>
        <thead>
          {headerGroups.map((headerGroup, row) => (
            <tr
              className={classnames('geo-header', { 'geo-header__bordered': row === 1 })}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column, i) => {
                return (
                  <th
                    className={classnames('geo-header__cell', {
                      'center-align': column.centerAlign || column.columns,
                      'geo-trend__header-cell': column.isGrouped,
                      'hidden-header': i === headerGroup.headers.length - 1 && showGrouped
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    width={column.width}
                  >
                    {column.render('Header')}
                    <span className="header-cell__sort-container">
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <i className="icon-sort-down" />
                        ) : (
                          <i className="icon-sort-up" />
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {rows.length > 0 && <tbody {...getTableBodyProps()}>{getTableRows()}</tbody>}
      </table>
    );
  }

  function renderContent() {
    return (
      <div className="geo-landing-page">
        {isPayScalePulseEnabled && (
          <section className="feature-content">
            <GeoLandingPageHeader
              handleCSVDownload={handleCSVDownload}
              history={history}
              isCompanyLocationTabEnabled={isCompanyLocationTabEnabled}
              landingPageURL={landingPageURL}
              selectedApplyStrategy={selectedApplyStrategy}
              setShowSettingsSlideout={setShowSettingsSlideout}
              setShowDataMethodologySlideout={setShowDataMethodologySlideout}
              showApplyGeo={showApplyGeo}
              showExploreGeo={showExploreGeo}
              csvLoading={csvLoading}
            />
            {isCompanyLocationTabEnabled ? (
              <Tabs
                className="tab-list"
                onTabClick={item => handleFeatureTabSwitch(parseInt(item))}
                selectedTabKey={selectedGeoFeatureTab.toString()}
              >
                <Tab title="Explore Geo Differentials">
                  <Tabs
                    className="tab-list secondary-tab__list"
                    onTabClick={item => handleExploreTabSwitch(parseInt(item))}
                    selectedTabKey={selectedExploreTab.toString()}
                  >
                    <Tab title="All Locations">{renderExploreTabContent()}</Tab>
                    <Tab title="Company Locations">{renderExploreTabContent()}</Tab>
                    <Tab title="Favorite Locations">{renderExploreTabContent()}</Tab>
                  </Tabs>
                </Tab>
                <Tab title="Apply Geo Differentials">{renderApplyTabContent()}</Tab>
              </Tabs>
            ) : (
              <Tabs
                className="tab-list"
                onTabClick={item => handleExploreTabSwitch(parseInt(item))}
                selectedTabKey={selectedExploreTab.toString()}
              >
                <Tab title="All Locations">{renderExploreTabContent()}</Tab>
                <Tab title="Favorite Locations">{renderExploreTabContent()}</Tab>
              </Tabs>
            )}
          </section>
        )}
        {isPayScalePulseEnabled !== undefined && !isPayScalePulseEnabled && !loading && <div>Not allowed</div>}
      </div>
    );
  }

  return (
    <>
      {renderContent()}
      <SettingsSlideout
        alertType={alertType}
        saveTrendSettings={actions.saveTrendSettings}
        setAppliedGeoDiffs={setAppliedGeoDiffs}
        setShowSettingsSlideout={setShowSettingsSlideout}
        settings={settings}
        showApplyGeo={showApplyGeo}
        showInternalDataSetting={showInternalDataSetting}
        showSettingsSlideout={showSettingsSlideout}
        showOnlySearch={!showPayScaleLocations || (showPayScaleLocations && hasData)}
        searchPayScaleLocations={actions.searchPayScaleLocations}
        supportedLocations={supportedLocations}
      />
      <DataMethodologySlideout
        showDataMethodologySlideout={showDataMethodologySlideout}
        setShowDataMethodologySlideout={setShowDataMethodologySlideout}
      />
    </>
  );
}

const mapStateToProps = ({ geoDifferential, session, app }) => ({
  alertType: geoDifferential.alertType,
  applyLocationColumns: geoDifferential.applyLocationColumns,
  applyLocations: geoDifferential.applyLocations,
  companyLocationColumns: geoDifferential.companyLocationColumns,
  companyLocations: geoDifferential.companyLocations,
  csvLoading: geoDifferential.csvLoading,
  employeeFilters: geoDifferential.employeeFilters,
  favoriteLocations: geoDifferential.favoriteLocations,
  groupId: Number(session?.group?.id),
  isApplyLocationsLoaded: geoDifferential.companyLocationsLoaded,
  isCompanyLocationsLoaded: geoDifferential.companyLocationsLoaded,
  isPayScaleLocationsLoaded: geoDifferential.payScaleLocationsLoaded,
  isSavingGeoDifferentials: geoDifferential.isSavingGeoDifferentials,
  isSearchingPayscaleLocations: geoDifferential.isSearchingPayscaleLocations,
  isSettingsLoaded: geoDifferential.isSettingsLoaded,
  loading:
    geoDifferential.payScaleLocationsLoading ||
    geoDifferential.companyLocationsLoading ||
    geoDifferential.favoriteLocationsLoading ||
    geoDifferential.applyLocationsLoading ||
    geoDifferential.isSettingsLoading ||
    geoDifferential.isSupportedLocationsLoading,
  orgLocations: geoDifferential.orgLocations,
  payScaleLocationColumns: geoDifferential.payScaleLocationColumns,
  payScaleLocationsLoading: geoDifferential.payScaleLocationsLoading,
  settings: geoDifferential.settings,
  supportedLocations: geoDifferential.supportedLocations,
  totalEmployees: geoDifferential.totalEmployees,
  totalLocations: geoDifferential.totalLocations,
  favoriteLocationsErrMsg: geoDifferential.favoriteLocationsErrMsg,
  favoriteLocationsErrType: geoDifferential.favoriteLocationsErrType,
  companyLocationsErrMsg: geoDifferential.companyLocationsErrMsg,
  companyLocationsErrType: geoDifferential.companyLocationsErrType,
  supportedLocationsErrMsg: geoDifferential.supportedLocationsErrMsg,
  supportedLocationsErrType: geoDifferential.supportedLocationsErrType,
  applyLocationsErrMsg: geoDifferential.applyLocationsErrMsg,
  applyLocationsErrType: geoDifferential.applyLocationsErrType
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      clearAlert,
      clearEmployeeFilter,
      downloadToCSV,
      getCompanyLocations,
      getTrendSettings,
      getPayScaleLocations,
      mapCompanyLocationToPayScaleLocation,
      getFavoriteLocations,
      addFavoriteLocation,
      removeFavoriteLocation,
      searchPayScaleLocations,
      saveTrendSettings,
      getSupportedPayScaleLocations,
      saveGeoDifferentials
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(GeoLandingPage);
